/**
 * Compact e-card template preview (still or muted video).
 *
 * Centralises the animated-template rules so confirmation shells, admin lists,
 * and future surfaces do not hand-roll {@code <img src={mediaToken}>} on MP4 blobs.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Box, Image } from '@mantine/core';
import {
  isEcardVideoTemplate,
  resolveEcardTemplateStillUrl,
  resolveEcardTemplateVideoUrl,
  type EcardTemplateMediaSource,
} from '../../utils/ecardTemplateMediaUrls';

export type EcardTemplateThumbnailProps = {
  source: EcardTemplateMediaSource | null | undefined;
  /** User upload override (confirmation after custom media). */
  pendingFile?: File | null;
  alt: string;
  width: number | string;
  height?: number | string;
  /** CSS aspect-ratio when height is omitted (default 1 for e-cards). */
  aspectRatio?: string;
  fit?: 'contain' | 'cover';
  radius?: string | number;
  /** Shown when media is missing or fails to load. */
  fallback?: ReactNode;
};

/**
 * Renders a still {@link Image} or muted {@code <video>} for template previews.
 */
export function EcardTemplateThumbnail({
  source,
  pendingFile,
  alt,
  width,
  height,
  aspectRatio = '1',
  fit = 'contain',
  radius = 'var(--mantine-radius-sm)',
  fallback = null,
}: EcardTemplateThumbnailProps) {
  const [failed, setFailed] = useState(false);
  const isVideo = !!source && isEcardVideoTemplate(source) && !pendingFile;
  const stillUrl = resolveEcardTemplateStillUrl(source, { pendingFile });
  const videoUrl = isVideo ? resolveEcardTemplateVideoUrl(source) : null;

  useEffect(() => {
    setFailed(false);
  }, [stillUrl, videoUrl, pendingFile, source?.mediaToken]);

  const boxStyle = {
    width,
    height: height ?? 'auto',
    aspectRatio: height ? undefined : aspectRatio,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--mantine-color-gray-1)',
    borderRadius: radius,
    overflow: 'hidden',
  } as const;

  if (failed || (!stillUrl && !videoUrl)) {
    return fallback ?? <Box style={boxStyle} aria-hidden />;
  }

  if (isVideo && videoUrl) {
    return (
      <Box
        component="video"
        src={videoUrl}
        poster={stillUrl ?? undefined}
        muted
        playsInline
        preload="metadata"
        style={{
          ...boxStyle,
          objectFit: fit,
          display: 'block',
        }}
        aria-label={alt}
        onError={() => setFailed(true)}
      />
    );
  }

  if (!stillUrl) {
    return fallback ?? <Box style={boxStyle} aria-hidden />;
  }

  return (
    <Box style={boxStyle}>
      <Image
        src={stillUrl}
        alt={alt}
        fit={fit}
        h="100%"
        w="100%"
        onError={() => setFailed(true)}
      />
    </Box>
  );
}
